import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PostCard from '../Feed/Post/PostCard';
import FeedActions from '../Feed/FeedActions';
import './ProfilePosts.scss';
import emptyIllustration from './img/empty.png';
import { getAccessToken } from '../../awsAuth';

import {
  cacheGetStale,
  cacheSet,
} from '../../utils/swrcache';

const TTL_2MIN = 2 * 60 * 1000;
const TTL_24H = 24 * 60 * 60 * 1000;

const getAuthHeaders = async () => {
  const token = await getAccessToken().catch(() => null);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function ProfilePosts({
  posts,
  userMeta,
  onPatchPosts,
  currentUserId,
  apiBase,
  timeAgo,
  deepItem,
  userId,
}) {
  const navigate = useNavigate();
  const [actionPost, setActionPost] = useState(null);

  const cacheKey = userId ? `profile:posts:${userId}` : null;

  const hydrated = useMemo(() => {
    if (!cacheKey) return null;
    const c = cacheGetStale(cacheKey, TTL_2MIN);
    return Array.isArray(c) ? c : null;
  }, [cacheKey]);

  const isLoading = !Array.isArray(posts) && !Array.isArray(hydrated);

  const effectivePosts = Array.isArray(posts)
    ? posts
    : (Array.isArray(hydrated) ? hydrated : null);

  useEffect(() => {
    if (!cacheKey) return;
    if (!Array.isArray(posts)) return;

    if (posts.length) {
      cacheSet(cacheKey, posts);
      return;
    }

    const existing = cacheGetStale(cacheKey, TTL_24H);
    if (!existing) cacheSet(cacheKey, []);
  }, [cacheKey, posts]);

  useEffect(() => {
    if (!cacheKey) return;
    if (!Array.isArray(effectivePosts)) return;

    if (effectivePosts.length) {
      cacheSet(cacheKey, effectivePosts);
    } else {
      const existing = cacheGetStale(cacheKey, TTL_24H);
      if (!existing) cacheSet(cacheKey, []);
    }
  }, [cacheKey, effectivePosts]);

  useEffect(() => {
    if (!deepItem) return;

    const tryScroll = () => {
      const escaped =
        typeof CSS !== 'undefined' && CSS.escape
          ? CSS.escape(String(deepItem))
          : String(deepItem);

      const el = document.querySelector(`[data-post-id="${escaped}"]`);

      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('pulse-highlight');
        setTimeout(() => el.classList.remove('pulse-highlight'), 1800);
        return true;
      }

      return false;
    };

    if (!tryScroll()) {
      const t = setTimeout(tryScroll, 300);
      const t2 = setTimeout(tryScroll, 800);
      return () => {
        clearTimeout(t);
        clearTimeout(t2);
      };
    }
  }, [deepItem]);

  const patchPostLocal = useCallback((postId, createdAt, patch) => {
    onPatchPosts(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];

      const next = safePrev.map(p => {
        const pid = String(p.postId || p.id || p._id);

        if (
          pid === String(postId) &&
          String(p.createdAt || '') === String(createdAt || '')
        ) {
          return { ...p, ...patch };
        }

        return p;
      });

      if (cacheKey) cacheSet(cacheKey, next);
      return next;
    });
  }, [onPatchPosts, cacheKey]);

  const handleToggleLike = async (post) => {
    if (!currentUserId) {
      navigate('/auth');
      return;
    }

    const pid = String(post.postId || post.id || post._id);
    const prevLiked = !!post.isLiked;

    const optimistic = {
      isLiked: !prevLiked,
      likesCount: Math.max(0, Number(post.likesCount || 0) + (prevLiked ? -1 : 1)),
    };

    patchPostLocal(pid, post.createdAt, optimistic);

    try {
      const authHeaders = await getAuthHeaders();

      const res = await fetch(
        `${apiBase}/api/posts/${encodeURIComponent(pid)}/like-toggle`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify({
            userId: currentUserId,
            createdAt: post.createdAt,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'like failed');

      try {
        const likR = await fetch(
          `${apiBase}/api/likes/${encodeURIComponent(pid)}`,
          {
            cache: 'no-store',
            headers: {
              ...authHeaders,
            },
          }
        );

        const lik = await likR.json().catch(() => ({}));

        const isLiked = Array.isArray(lik?.users)
          ? lik.users.includes(currentUserId)
          : optimistic.isLiked;

        const likesCount = Number(lik?.count ?? optimistic.likesCount);

        patchPostLocal(pid, post.createdAt, {
          isLiked,
          likesCount: Number.isFinite(likesCount) ? likesCount : optimistic.likesCount,
        });
      } catch {
        patchPostLocal(pid, post.createdAt, {
          isLiked:
            typeof data.liked !== 'undefined'
              ? !!data.liked
              : optimistic.isLiked,
          likesCount:
            typeof data.likesCount === 'number'
              ? data.likesCount
              : optimistic.likesCount,
        });
      }
    } catch {
      patchPostLocal(pid, post.createdAt, {
        isLiked: prevLiked,
        likesCount: post.likesCount || 0,
      });
    }
  };

  const handleToggleSave = async (post) => {
    if (!currentUserId) {
      navigate('/auth');
      return;
    }

    const pid = String(post.postId || post.id || post._id);
    const prevSaved = !!post.isSaved;

    patchPostLocal(pid, post.createdAt, { isSaved: !prevSaved });

    try {
      const authHeaders = await getAuthHeaders();

      if (prevSaved) {
        await fetch(`${apiBase}/api/saved`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify({
            userId: currentUserId,
            postId: pid,
          }),
        });
      } else {
        await fetch(`${apiBase}/api/saved`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify({
            userId: currentUserId,
            postId: pid,
            type: 'post',
            collectionId: 'all',
          }),
        });
      }

      try {
        const c = await fetch(
          `${apiBase}/api/saved/count/${encodeURIComponent(pid)}`,
          {
            cache: 'no-store',
            headers: {
              ...authHeaders,
            },
          }
        );

        const json = await c.json().catch(() => ({}));
        const n = Number(json?.count);

        if (Number.isFinite(n)) {
          patchPostLocal(pid, post.createdAt, {
            favoritesCount: n,
            isSaved: !prevSaved,
          });
        } else {
          patchPostLocal(pid, post.createdAt, { isSaved: !prevSaved });
        }
      } catch {
        patchPostLocal(pid, post.createdAt, { isSaved: !prevSaved });
      }
    } catch {
      patchPostLocal(pid, post.createdAt, { isSaved: prevSaved });
    }
  };

  const handleShareIncrement = async (pid, createdAt) => {
    try {
      const authHeaders = await getAuthHeaders();

      const r = await fetch(
        `${apiBase}/api/shares/count/${encodeURIComponent(pid)}`,
        {
          cache: 'no-store',
          headers: {
            ...authHeaders,
          },
        }
      );

      const j = await r.json().catch(() => ({}));
      const n = Number(j?.count || 0);

      patchPostLocal(pid, createdAt, { sharesCount: n });
    } catch {}
  };

  const handleSyncCounts = useCallback((patch) => {
    const pid = patch?.postId || patch?.listingId;
    if (!pid) return;

    onPatchPosts(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];

      const next = safePrev.map(p => {
        const currentPid = String(p.postId || p.id || p._id);
        if (currentPid !== String(pid)) return p;

        return {
          ...p,
          ...(typeof patch.likesCount === 'number'
            ? { likesCount: Math.max(0, Number(patch.likesCount || 0)) }
            : {}),
          ...(typeof patch.commentsCount === 'number'
            ? { commentsCount: Math.max(0, Number(patch.commentsCount || 0)) }
            : {}),
          ...(typeof patch.sharesCount === 'number'
            ? { sharesCount: Math.max(0, Number(patch.sharesCount || 0)) }
            : {}),
          ...(typeof patch.saved === 'boolean'
            ? { isSaved: patch.saved }
            : {}),
          ...(typeof patch.liked === 'boolean'
            ? { isLiked: patch.liked }
            : {}),
        };
      });

      if (cacheKey) cacheSet(cacheKey, next);
      return next;
    });
  }, [onPatchPosts, cacheKey]);

  useEffect(() => {
    const onLikes = (e) => {
      const d = e?.detail || {};
      const pid = d.postId || d.listingId;
      if (!pid) return;

      onPatchPosts(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];

        const next = safePrev.map(p => {
          const currentPid = String(p.postId || p.id || p._id);
          if (currentPid !== String(pid)) return p;

          let nextLikesCount = Number(p.likesCount || 0);

          if (typeof d.absolute === 'number') {
            nextLikesCount = Math.max(0, Number(d.absolute || 0));
          } else if (typeof d.delta === 'number') {
            nextLikesCount = Math.max(0, nextLikesCount + Number(d.delta || 0));
          }

          return {
            ...p,
            likesCount: nextLikesCount,
            ...(typeof d.liked === 'boolean' ? { isLiked: d.liked } : {}),
          };
        });

        if (cacheKey) cacheSet(cacheKey, next);
        return next;
      });
    };

    const onSaved = (e) => {
      const d = e?.detail || {};
      const pid = d.postId || d.listingId;
      if (!pid) return;

      onPatchPosts(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];

        const next = safePrev.map(p => {
          const currentPid = String(p.postId || p.id || p._id);
          if (currentPid !== String(pid)) return p;

          return {
            ...p,
            ...(typeof d.saved === 'boolean' ? { isSaved: d.saved } : {}),
          };
        });

        if (cacheKey) cacheSet(cacheKey, next);
        return next;
      });
    };

    const onComments = (e) => {
      const d = e?.detail || {};
      const pid = d.postId || d.listingId;
      if (!pid) return;

      onPatchPosts(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];

        const next = safePrev.map(p => {
          const currentPid = String(p.postId || p.id || p._id);
          if (currentPid !== String(pid)) return p;

          let nextCommentsCount = Number(p.commentsCount || 0);

          if (typeof d.absolute === 'number') {
            nextCommentsCount = Math.max(0, Number(d.absolute || 0));
          } else if (typeof d.delta === 'number') {
            nextCommentsCount = Math.max(0, nextCommentsCount + Number(d.delta || 0));
          }

          return {
            ...p,
            commentsCount: nextCommentsCount,
          };
        });

        if (cacheKey) cacheSet(cacheKey, next);
        return next;
      });
    };

    const onShares = (e) => {
      const d = e?.detail || {};
      const pid = d.postId || d.listingId;
      if (!pid) return;

      onPatchPosts(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];

        const next = safePrev.map(p => {
          const currentPid = String(p.postId || p.id || p._id);
          if (currentPid !== String(pid)) return p;

          let nextSharesCount = Number(p.sharesCount || 0);

          if (typeof d.absolute === 'number') {
            nextSharesCount = Math.max(0, Number(d.absolute || 0));
          } else if (typeof d.delta === 'number') {
            nextSharesCount = Math.max(0, nextSharesCount + Number(d.delta || 0));
          }

          return {
            ...p,
            sharesCount: nextSharesCount,
          };
        });

        if (cacheKey) cacheSet(cacheKey, next);
        return next;
      });
    };

    window.addEventListener('cv-likes-updated', onLikes);
    window.addEventListener('cv-saved-updated', onSaved);
    window.addEventListener('cv-comments-updated', onComments);
    window.addEventListener('cv-shares-updated', onShares);

    return () => {
      window.removeEventListener('cv-likes-updated', onLikes);
      window.removeEventListener('cv-saved-updated', onSaved);
      window.removeEventListener('cv-comments-updated', onComments);
      window.removeEventListener('cv-shares-updated', onShares);
    };
  }, [onPatchPosts, cacheKey]);

  const handleDeletePost = useCallback(
    (type, id) => {
      onPatchPosts(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];
        const next = safePrev.filter(p => String(p.postId || p.id || p._id) !== String(id));
        if (cacheKey) cacheSet(cacheKey, next);
        return next;
      });
    },
    [onPatchPosts, cacheKey]
  );

  const buildProfilePostUrl = useCallback((postId, ownerId) => {
    const profileId = ownerId || userId || '';
    if (!profileId || !postId) return '';

    return (
      `${window.location.origin}/profile/${encodeURIComponent(profileId)}` +
      `?tab=posts&item=${encodeURIComponent(postId)}`
    );
  }, [userId]);

  if (isLoading) {
    return (
      <div className="profile-content-section">
        <div className="profile-feed feed profile-feed--skeleton">
          {Array.from({ length: 3 }).map((_, idx) => (
            <div key={idx} className="feed__card-wrapper feed__card-wrapper--skeleton">
              <div className="post-skel-header" />
              <div className="post-skel-body" />
              <div className="post-skel-footer" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!effectivePosts || effectivePosts.length === 0) {
    return (
      <div className="profile-content-section">
        <div className="empty-state" role="status" aria-live="polite">
          <img className="empty-state__img" src={emptyIllustration} alt="" />
          <p className="empty-state__text">Поки що немає постів</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-content-section">
      <div className="profile-feed feed profile-posts-feed">
        {effectivePosts.map((post) => {
          const pid = String(post.postId || post.id || post._id);
          const key = pid + ':' + (post.createdAt || 0);
          const isUnderReview = !!post.__autoHidden;

          const authorId =
            post.userId ||
            post.authorId ||
            post.ownerId ||
            userMeta?.effectiveUserId ||
            userId ||
            '';

          const itemForCard = {
            ...post,
            id: post.id || pid,
            postId: pid,
            userId: authorId,
            authorId,
            ownerId: authorId,
            authorName:
              post.authorName ||
              post.username ||
              userMeta?.userName ||
              'Користувач',
            authorAvatar:
              post.authorAvatar ||
              post.avatar ||
              userMeta?.avatar ||
              '',
            text:
              post.text ||
              post.description ||
              post.caption ||
              '',
            description:
              post.description ||
              post.text ||
              post.caption ||
              '',
            photo:
              post.photo ||
              post.image ||
              post.imageUrl ||
              '',
            likesCount: Number(post.likesCount || 0),
            commentsCount: Number(post.commentsCount || 0),
            sharesCount: Number(post.sharesCount || 0),
            favoritesCount: Number(post.favoritesCount || post.savedCount || 0),
            isLiked: !!post.isLiked,
            isSaved: !!post.isSaved,
          };

          return (
            <div
              key={key}
              className={`feed__card-wrapper ${
                isUnderReview ? 'post-under-review' : ''
              }`}
              data-post-id={pid}
            >
              <PostCard
                item={itemForCard}
                isSaved={!!itemForCard.isSaved}
                isLiked={!!itemForCard.isLiked}
                likesCount={Number(itemForCard.likesCount || 0)}
                onToggleLike={() => handleToggleLike(itemForCard)}
                onToggleSave={() => handleToggleSave(itemForCard)}
                onOpenActions={() => setActionPost(itemForCard)}
                navigate={navigate}
                timeAgo={timeAgo(itemForCard.createdAt)}
                ownerView={String(currentUserId || '') === String(authorId || '')}
                onShareIncrement={() => handleShareIncrement(pid, itemForCard.createdAt)}
                onSyncCounts={handleSyncCounts}
                allowComments={true}
              />

              {isUnderReview && (
                <div className="post-review-overlay">
                  <div className="post-review-badge">Приховано через скарги</div>
                  <div className="post-review-text">
                    Очікує рішення модератора
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {actionPost && (() => {
        const sharePostId = String(
          actionPost.postId || actionPost.id || actionPost._id
        );

        const shareUserId =
          actionPost.userId ||
          actionPost.authorId ||
          actionPost.ownerId ||
          userMeta?.effectiveUserId ||
          userId ||
          '';

        return (
          <FeedActions
            type="post"
            id={sharePostId}
            createdAt={actionPost.createdAt}
            isOwner={String(currentUserId || '') === String(shareUserId || '')}
            onClose={() => setActionPost(null)}
            onDelete={handleDeletePost}
            authorId={shareUserId}
            authorName={actionPost.authorName || userMeta?.userName || 'Автор'}
            postText={actionPost.description || actionPost.text || ''}
            postUrl={buildProfilePostUrl(sharePostId, shareUserId)}
            category={actionPost.category || ''}
          />
        );
      })()}
    </div>
  );
}